import React, { memo, useMemo } from 'react';
import { TableBody, TableCell, TableRow, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import { capitalCase } from 'change-case';
import classNames from 'classnames';
import { TimeSignalAttribute } from 'graphql/types';
import { validateAndFormatStringAttribute } from 'utils/url-validator';
import { useMeContext } from 'modules/me';
import { getLuxonJsDateTimeFormatFromMe } from 'utils/date-time';
import NoteAttributeValue from './NoteAttributeValue';

const useStyles = makeStyles((theme) => ({
  tableRow: {
    verticalAlign: 'top'
  },
  tableCell: {
    padding: theme.spacing(0.5),
    borderBottom: 'none'
  },
  text: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.caption.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.caption.fontWeight
  },
  heading: {
    minWidth: theme.spacing(14),
    maxWidth: theme.spacing(50),
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1)
  }
}));

type NoteAttributeProps = {
  attribute: TimeSignalAttribute;
};

export enum AttributeType {
  LIST,
  LINK,
  TEXT
}

interface FormattedAttributeValue {
  value: string;
  type: AttributeType;
}

const isDomainAttribute = (attribute: TimeSignalAttribute) => {
  const browserCollectorIds = [
    'fbb88540f6834b9db09a79fc891e3485',
    '1c8be8050d6341bcaa07bc6daa78b54f',
    '213bf8eb6a9241a290c92186db243a90'
  ];
  return (
    attribute.name === 'domains' &&
    browserCollectorIds.indexOf(attribute.sourceId) >= 0
  );
};
const getFormattedAttributeValue = (
  attribute: TimeSignalAttribute,
  format: string
): FormattedAttributeValue => {
  if (attribute.value.type === 'timestamp') {
    return {
      value: DateTime.fromISO(attribute.value.value).toFormat(format),
      type: AttributeType.TEXT
    };
  } else if (attribute.value.type == 'list(string)') {
    const type = isDomainAttribute(attribute)
      ? AttributeType.LIST
      : AttributeType.TEXT;
    const value = isDomainAttribute(attribute)
      ? attribute.value.value
      : attribute.value.value
          .map((v: string) => {
            const { value } = validateAndFormatStringAttribute(v);
            return value;
          })
          .join(', ');
    return {
      type,
      value
    };
  }
  const attributeValue = validateAndFormatStringAttribute(
    attribute.value.value
  );
  const type = attributeValue.isLink ? AttributeType.LINK : AttributeType.TEXT;
  return {
    value: attributeValue.value,
    type: type
  };
};

export const NoteAttribute: React.FC<NoteAttributeProps> = ({
  attribute
}: NoteAttributeProps) => {
  const classes = useStyles();
  const me = useMeContext();
  const format = getLuxonJsDateTimeFormatFromMe(me);
  const { value, type } = useMemo(
    () => getFormattedAttributeValue(attribute, format),
    [attribute, format]
  );

  const fallBackAttributeName = useMemo(() => capitalCase(attribute.name), [
    attribute.name
  ]);

  return (
    <TableBody>
      <TableRow
        data-testid="note-individual-attribute"
        className={classes.tableRow}
      >
        <TableCell className={classNames(classes.heading, classes.tableCell)}>
          <Typography noWrap className={classes.text}>
            <FormattedMessage
              id={`note.attributes.more.${attribute.name}`}
              defaultMessage={fallBackAttributeName}
            />
          </Typography>
        </TableCell>
        <TableCell className={classes.tableCell}>
          <NoteAttributeValue
            attribute={attribute}
            value={value}
            type={type}
            textClassName={classes.text}
          />
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

export default memo(NoteAttribute);
